import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileUploadControl } from '@iplab/ngx-file-upload';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { ScDialogService } from '../../../sc-dialogs/sc-dialog.service';

export type UploadViewModes = 'list' | 'single' | 'preview';
export type UploadScrollModes = 'vertical' | 'horizontal';

@Component({
  selector: 'sc-drop-box-upload',
  templateUrl: './drop-box-upload.component.html',
  styleUrls: ['./drop-box-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropBoxUploadComponent),
      multi: true
    }
  ]
})
export class DropBoxUploadComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() limit: number;
  @Input() disabled: boolean;
  @Input() title: string;

  @Output() filesChange: EventEmitter<File[]> = new EventEmitter();
  @Output() fileChange: EventEmitter<File> = new EventEmitter();

  public files$: Observable<{ file: File; url: string }[]>;
  public fileUploadControl: FileUploadControl = new FileUploadControl();

  private _urls: any[] = [];
  private _files: File[] = [];
  private _mode: UploadViewModes = 'list';
  private _scrollMode: UploadScrollModes = 'vertical';
  private _filesSubject$: BehaviorSubject<File[]> = new BehaviorSubject([]);
  private _subscription$: Subject<void> = new Subject();

  constructor(private _dialogService: ScDialogService) {}

  @Input() set urls(urls: string[]) {
    if (urls && urls.length) {
      this._urls = urls.map(url => ({
        url
      }));
    }
  }

  get urls() {
    return this._urls;
  }

  @Input() set files(files: File[]) {
    if (files && files.length) {
      this._files = files;
      this._filesSubject$.next(files);
    }
  }

  get files(): File[] {
    return this._files;
  }

  @Input() set file(file: File) {
    if (file) {
      this._files = [file];
      this._filesSubject$.next(this._files);
    }
  }

  @Input() set mode(m: UploadViewModes) {
    if (!m) {
      m = 'list';
    }
    this._mode = m;
  }
  get mode() {
    return this._mode;
  }

  @Input() set scrollMode(s: UploadScrollModes) {
    if (!s) {
      s = 'horizontal';
    }
    this._scrollMode = s;
  }

  get scrollMode() {
    return this._scrollMode;
  }

  onChange = (files: File | File[]) => {};
  onTouched = () => {};

  public ngOnInit() {
    this.fileUploadControl.setListVisibility(false);
    this.files$ = this._filesSubject$.asObservable().pipe(
      switchMap((files: File[]) => {
        if (this.limit && files.length > this.limit) {
          files = files.slice(0, this.limit);
        }

        if (!files.length) {
          return of([]);
        }

        const fs = files.map(file => this._getUrl(file));
        return combineLatest(fs);
      }),
      map((urls: string[]) =>
        urls.map((url, index) => ({
          file: this.fileUploadControl.value[index],
          url
        }))
      )
    );
    this.fileUploadControl.valueChanges
      .pipe(
        takeUntil(this._subscription$),
        startWith(this._files)
      )
      .subscribe(files => {
        if (files && files.length) {
          files = files.filter(f => !!f);
        }
        if (this.limit && files.length > this.limit) {
          files = files.slice(0, this.limit);
        }

        this.onChange(this.mode === 'single' ? files[0] : files);

        if (this.mode === 'single') {
          this.fileChange.emit(files[0]);
        } else {
          this.filesChange.emit(files);
        }

        this._filesSubject$.next(files);
      });
  }

  public remove(file: File) {
    const files: File[] = this.fileUploadControl.value;
    files.splice(files.indexOf(file), 1);
    this.fileUploadControl.setValue(files);
  }

  public openDialog(urls: { url: string }[]) {
    const previewerUrls = urls.map(({ url }) => ({
      small: url,
      medium: url
    }));
    this._dialogService.imagePreviewer(previewerUrls);
  }

  public writeValue(files: File | File[]) {
    if (!Array.isArray(files)) {
      files = [files as File];
    }

    this._files = files as File[];
    this._filesSubject$.next(files as File[]);
    this.fileUploadControl.setValue(files as File[]);
    this.onChange(this.mode === 'single' ? files[0] : files);
  }

  public registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public trackByIndex = (index: number): number => index;

  public ngOnDestroy() {
    this._subscription$.next();
    this._subscription$.complete();
  }

  private _getUrl(file: File) {
    return new Observable(observer => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        observer.next(reader.result as string);
      };
    });
  }
}
